import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Download, AlertCircle, CheckCircle2, Lock, FileText, Rewind, Lock as LockIcon } from "lucide-react";

const STATUS_COLORS = {
  collecte: "bg-slate-100 text-slate-700",
  fige: "bg-amber-100 text-amber-800",
  retour_anomalies: "bg-red-100 text-red-800",
  cloture: "bg-emerald-100 text-emerald-800",
  superseded_by: "bg-slate-200 text-slate-500 line-through",
};

export default function PreparationPaie() {
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periode, setPeriode] = useState(() => new Date().toISOString().slice(0, 7));
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/payroll/lots"); setLots(data || []); }
    catch { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createLot = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/payroll/lots", { periode });
      toast.success(`Lot ${data.periode} créé — ${data.dossiers.length} dossier(s), ${data.anomalies_lot?.length || 0} anomalie(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur création"); }
    finally { setCreating(false); }
  };

  const freeze = async (id) => {
    try { await api.post(`/payroll/lots/${id}/freeze`); toast.success("Lot figé"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const exportCsv = async (id, periode, version) => {
    try {
      const r = await api.get(`/payroll/lots/${id}/export.csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `PRECOMPTA_PAIE_${periode}_v${version}.csv`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Erreur export"); }
  };
  const correction = async (id) => {
    try { const { data } = await api.post(`/payroll/lots/${id}/correction`); toast.success(`Version ${data.version} créée`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const cloture = async (id) => {
    try { await api.post(`/payroll/lots/${id}/cloture`); toast.success("Lot clôturé — paie indépendante du règlement"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-6" data-testid="preparation-paie-page">
      <PageHeader title="Préparation paie mensuelle" subtitle="Workflow comptable : collecte → contrôle GE → lot figé → export → anomalies → corrections versionnées → clôture" />
      <Card className="p-4 bg-amber-50 border-amber-200">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <strong>Précomptabilité, pas FEC.</strong> L'export est un format <em>générique</em> à transmettre
            au cabinet comptable. Aucune connexion comptable n'est automatisée. La paie des salariés est
            <strong> indépendante du règlement</strong> des entreprises utilisatrices.
          </div>
        </div>
      </Card>

      <Card className="p-4">
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="text-xs text-slate-600 uppercase tracking-wider">Période</label>
            <Input type="month" value={periode} onChange={(e) => setPeriode(e.target.value)} data-testid="periode-input" />
          </div>
          <Button onClick={createLot} disabled={creating} data-testid="create-lot-btn">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Lancer collecte"}
          </Button>
        </div>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="space-y-3">
          {lots.map((lot) => (
            <Card key={lot.id} className="p-4" data-testid={`lot-${lot.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-lg">{lot.periode}</span>
                    <Badge className={STATUS_COLORS[lot.status] || ""}>{lot.status}</Badge>
                    <Badge variant="outline">v{lot.version}</Badge>
                    {lot.parent_lot_id && <Badge variant="outline">correction de v{lot.version - 1}</Badge>}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    Créé le {new Date(lot.created_at).toLocaleString("fr-FR")} par {lot.created_by_email}
                  </div>
                  <div className="text-sm text-slate-600 mt-2">
                    {lot.dossiers?.length || 0} dossier(s), {lot.entreprises_concernees?.length || 0} entreprise(s)
                    {lot.anomalies_lot?.length > 0 && (
                      <span className="ml-2 text-amber-700">⚠ {lot.anomalies_lot.length} anomalie(s)</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  {lot.status === "collecte" && (
                    <Button size="sm" variant="outline" onClick={() => freeze(lot.id)} data-testid={`freeze-${lot.id}`}>
                      <LockIcon className="h-3.5 w-3.5 mr-1" /> Figer
                    </Button>
                  )}
                  {["fige", "retour_anomalies", "cloture"].includes(lot.status) && (
                    <Button size="sm" variant="outline" onClick={() => exportCsv(lot.id, lot.periode, lot.version)} data-testid={`export-${lot.id}`}>
                      <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
                    </Button>
                  )}
                  {lot.status === "retour_anomalies" && (
                    <Button size="sm" variant="outline" onClick={() => correction(lot.id)} data-testid={`correct-${lot.id}`}>
                      <Rewind className="h-3.5 w-3.5 mr-1" /> Correction v+1
                    </Button>
                  )}
                  {["fige", "retour_anomalies"].includes(lot.status) && (
                    <Button size="sm" onClick={() => cloture(lot.id)} data-testid={`cloture-${lot.id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Clôturer
                    </Button>
                  )}
                </div>
              </div>
              {lot.anomalies_lot?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <div className="text-xs font-medium text-amber-800 mb-1">Anomalies détectées :</div>
                  <ul className="text-xs text-slate-600 space-y-0.5 max-h-32 overflow-auto">
                    {lot.anomalies_lot.slice(0, 10).map((a, i) => (
                      <li key={i}>• {a.salarie_id?.slice(0, 8)} — {a.msg}</li>
                    ))}
                    {lot.anomalies_lot.length > 10 && <li className="italic text-slate-500">... et {lot.anomalies_lot.length - 10} autres</li>}
                  </ul>
                </div>
              )}
            </Card>
          ))}
          {lots.length === 0 && (
            <div className="text-center py-8 text-slate-500 text-sm">Aucun lot préparé — lancez une collecte sur une période.</div>
          )}
        </div>
      )}
    </div>
  );
}
